import apiClient from './apiClient';
import { readPage } from '../utils/paginatedResult';
import {
  Vendor,
  VendorProduct,
  VendorsApiResponse,
  SingleVendorApiResponse,
  VendorMutationResponse,
  VendorProductsApiResponse,
  ProductMutationResponse,
  DeleteApiResponse
} from '../types/vendor';
import { isValidEmailAddress, normalizeEmailAddress } from '../utils/emailValidation';
 
const VENDOR_PATH = 'vendor';

export async function getVendorOrders<T>(vendorId: string, page: number, signal?: AbortSignal) {
  return readPage<T>((await apiClient.get(`${VENDOR_PATH}/${encodeURIComponent(vendorId)}/orders`, { params: { page, limit: 20 }, signal })).data, 'results');
}

/** Nest validation pipe rejects properties not listed on the DTO. */
const buildVendorCreatePayload = (
  data: Record<string, unknown>
): Record<string, unknown> => {
  const payload: Record<string, unknown> = {
    name: String(data.name ?? '').trim(),
  };

  const email = String(data.email ?? '').trim();
  if (email) {
    const normalizedEmail = normalizeEmailAddress(email);
    if (!isValidEmailAddress(normalizedEmail)) throw new Error('Enter a valid vendor email address.');
    payload.email = normalizedEmail;
  }

  const phone = String(data.phone ?? '').trim();
  if (phone) payload.phone = phone;

  const address = String(data.address ?? '').trim();
  if (address) payload.address = address;

  const description = String(data.description ?? '').trim();
  if (description) payload.description = description;

  const logo = String(data.logo ?? '').trim();
  if (logo) payload.logo = logo;

  if (typeof data.isActive === 'boolean') {
    payload.isActive = data.isActive;
  }

  return payload;
};

const buildVendorUpdatePayload = (
  data: Record<string, unknown>
): Record<string, unknown> => {
  const payload: Record<string, unknown> = {};

  if (data.name !== undefined && String(data.name).trim()) {
    payload.name = String(data.name).trim();
  }
  if (data.email !== undefined) {
    const email = String(data.email).trim();
    if (email) {
      const normalizedEmail = normalizeEmailAddress(email);
      if (!isValidEmailAddress(normalizedEmail)) throw new Error('Enter a valid vendor email address.');
      payload.email = normalizedEmail;
    }
  }
  if (data.phone !== undefined) {
    const phone = String(data.phone).trim();
    if (phone) payload.phone = phone;
  }
  if (data.address !== undefined) {
    payload.address = String(data.address).trim();
  }
  if (data.description !== undefined) {
    payload.description = String(data.description).trim();
  }
  if (data.logo !== undefined) {
    const logo = String(data.logo).trim();
    if (logo) payload.logo = logo;
  }
  if (typeof data.isActive === 'boolean') {
    payload.isActive = data.isActive;
  }

  return payload;
};

const normalizeVendor = (body: unknown): Vendor => {
  const raw = body as Record<string, unknown>;
  return (raw?.vendor ?? raw?.data ?? raw) as Vendor;
};

export const getApiErrorMessage = (error: unknown, fallback: string): string => {
  const err = error as { response?: { data?: { message?: string | string[] } } };
  const message = err.response?.data?.message;
  if (Array.isArray(message)) return message.join(', ');
  if (typeof message === 'string') return message;
  return fallback;
};

// Updated to accept 'search'
export const getVendors = async (
  page = 1, 
  limit = 10,
  search = '' // New parameter
): Promise<VendorsApiResponse> => {
  try {
    // Construct params dynamically
    const params: Record<string, string> = {};
    if (search.trim()) params.search = search.trim();

    const response = await apiClient.get(`${VENDOR_PATH}/all`, { params });
    const body = response.data;
    const vendors: Vendor[] = Array.isArray(body) ? body : body?.data;
    if (!Array.isArray(vendors)) throw new Error('Unable to read the vendor list.');
    return {
      success: true,
      data: vendors.slice((page - 1) * limit, page * limit),
      count: vendors.length,
    };
  } catch (error) {

    throw error;
  }
};

// ... keep the rest of the file exactly as it was ...
export const getVendorById = async (vendorId: string): Promise<SingleVendorApiResponse> => {
  try {
    const response = await apiClient.get(`${VENDOR_PATH}/${vendorId}`);
    const vendor = normalizeVendor(response.data);
    return { success: true, data: vendor };
  } catch (error) {

    throw error;
  }
};

export const createVendor = async (vendorData: Omit<Vendor, 'id' | 'createdAt' | 'updatedAt'>): Promise<VendorMutationResponse> => {
  try {
    const response = await apiClient.post(
      `${VENDOR_PATH}/create`,
      buildVendorCreatePayload(vendorData as Record<string, unknown>)
    );
    const body = response.data as Record<string, unknown>;
    const vendor = normalizeVendor(body);
    return {
      success: true,
      message: (body?.message as string) ?? 'Vendor created successfully',
      vendor,
    };
  } catch (error) {

    throw error;
  }
};

export const updateVendor = async (vendorId: string, updates: Partial<Vendor>): Promise<VendorMutationResponse> => {
  try {
    const response = await apiClient.patch(
      `${VENDOR_PATH}/${vendorId}`,
      buildVendorUpdatePayload(updates as Record<string, unknown>)
    );
    const body = response.data as Record<string, unknown>;
    const vendor = normalizeVendor(body);
    return {
      success: true,
      message: (body?.message as string) ?? 'Vendor updated successfully',
      vendor,
    };
  } catch (error) {

    throw error;
  }
};

export const deleteVendor = async (vendorId: string): Promise<DeleteApiResponse> => {
  try {
    const response = await apiClient.delete<DeleteApiResponse>(`${VENDOR_PATH}/${vendorId}`);
    return response.data;
  } catch (error) {

    throw error;
  }
};
  
export const getVendorProducts = (vendorId: string, page = 1, limit = 20): Promise<VendorProductsApiResponse> =>
  getAllVendorProducts(page, limit, { vendorId });

/**
 * Naija Shop: fetch ALL vendor products across vendors (paginated, filterable).
 * GET /admins/vendor/products/all
 */
export const getAllVendorProducts = async (
  page = 1,
  limit = 10,
  filters: { search?: string; category?: string; vendorId?: string; status?: string; minPriceNGN?: number; maxPriceNGN?: number; minPriceGBP?: number; maxPriceGBP?: number } = {}
): Promise<VendorProductsApiResponse> => {
  try {
    const params: any = { page, limit };
    for (const [key, value] of Object.entries(filters)) {
      if (value !== undefined && value !== '') params[key] = value;
    }

    const response = await apiClient.get<VendorProductsApiResponse>(
      `${VENDOR_PATH}/products/all`,
      { params }
    );
    const result = readPage<VendorProduct>(response.data, 'results');
    return { success: true, results: result.items, pagination: { currentPage: result.page, totalPages: result.totalPages, totalItems: result.total } };
  } catch (error) {

    throw error;
  }
};
 
export const createVendorProduct = async (
  vendorId: string,
  productData: FormData
): Promise<ProductMutationResponse> => {
  try {
    // Let axios set multipart boundary — do NOT set Content-Type manually
    const response = await apiClient.post<ProductMutationResponse>(
      `${VENDOR_PATH}/${vendorId}/products/create`,
      productData
    );
    return response.data;
  } catch (error: any) {

    throw error;
  }
};

export const updateVendorProduct = async (productId: string, updates: Partial<VendorProduct> | FormData): Promise<ProductMutationResponse> => {
  try {
    const response = await apiClient.patch<ProductMutationResponse>(`${VENDOR_PATH}/products/${productId}`, updates);
    return response.data;
  } catch (error) {

    throw error;
  }
};
 
export const updateProductPricing = async (
    productId: string,
    pricingUpdates: { priceNGN?: number; priceGBP?: number; availableInNigeria?: boolean; availableInUK?: boolean }
): Promise<ProductMutationResponse> => {
  try {
    const response = await apiClient.patch<ProductMutationResponse>(`${VENDOR_PATH}/products/${productId}/pricing`, pricingUpdates);
    return response.data;
  } catch (error) {

    throw error;
  }
};
 
export const setProductFeatured = async (
  productId: string,
  isFeatured: boolean
): Promise<ProductMutationResponse> => {
  try {
    const response = await apiClient.patch<ProductMutationResponse>(
      `${VENDOR_PATH}/products/${productId}/featured`,
      { isFeatured }  
    );
    return response.data;
  } catch (error: any) {

    throw error;
  }
};

export const deleteVendorProduct = async (productId: string): Promise<DeleteApiResponse> => {
    try {
        const response = await apiClient.delete<DeleteApiResponse>(`${VENDOR_PATH}/products/${productId}`);
        return response.data;
    } catch (error) {

        throw error;
    }
};
